// assets/js/components/revenue-calculator/ui.js
//
// Plain HTML-string builders replacing the spec's React components
// (TInput/SSelect/Radio/Chk/Card/SLbl/H2/Sub/PBtn). Each returns a string;
// callers set it via innerHTML and bind events themselves (see steps/*.js).

function esc(s) {
    return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function fieldError(message) {
    return '<p class="rc-calc__field-error" role="alert">' + esc(message) + "</p>";
}

// Injects aria-describedby (pointing at the hint/error ids) and, when required,
// required/aria-required onto the field's actual control — targets the first
// tag of `inner`, which is always a single <input>/<select>/<div role="radiogroup">
// built by textInput()/select()/radioGroup() (never raw multi-root markup).
function describeControl(inner, describedBy, required) {
    var attrs = ' aria-describedby="' + describedBy + '"';
    if (/^<(input|select)\b/.test(inner)) {
        return inner.replace(/^<(input|select)\b/, "<$1" + attrs + (required ? ' required aria-required="true"' : ""));
    }
    if (/^<div\b/.test(inner)) {
        return inner.replace(/^<div\b/, "<div" + attrs + (required ? ' aria-required="true"' : ""));
    }
    return inner;
}

function field(opts) {
    var required = opts.required ? '<span class="rc-calc__required" aria-hidden="true">*</span>' : "";
    var optionalTag = !opts.required ? '<span class="rc-calc__optional">' + _t("(optional)") + "</span>" : "";
    var hintId = opts.hint ? opts.id + "-hint" : "";
    var errorId = opts.id + "-error";
    var hint = opts.hint ? '<p class="rc-calc__hint" id="' + hintId + '">' + esc(opts.hint) + "</p>" : "";
    var errorSlot = '<div class="rc-calc__field-error-slot" id="' + errorId + '" data-error-for="' + opts.id + '"></div>';
    var describedBy = (hintId + " " + errorId).trim();
    return (
        '<div class="rc-calc__field" data-field="' + opts.id + '">' +
        '<label class="label rc-calc__label" for="' + opts.id + '">' + esc(opts.label) + required + optionalTag + "</label>" +
        hint + describeControl(opts.inner, describedBy, opts.required) + errorSlot +
        "</div>"
    );
}

function textInput(opts) {
    var min = opts.min != null ? ' min="' + opts.min + '"' : "";
    var max = opts.max != null ? ' max="' + opts.max + '"' : "";
    var value = opts.value != null ? esc(opts.value) : "";
    return (
        '<input class="rc-calc__input" id="' + opts.id + '" type="' + (opts.type || "text") + '"' +
        ' value="' + value + '" placeholder="' + esc(opts.placeholder || "") + '"' + min + max + ">"
    );
}

function select(opts) {
    var optionsHtml = (opts.options || []).map(function (o) {
        var val = typeof o === "string" ? o : o.value;
        var label = typeof o === "string" ? o : o.label;
        var selected = opts.value === val ? " selected" : "";
        return '<option value="' + esc(val) + '"' + selected + ">" + esc(label) + "</option>";
    }).join("");
    return (
        '<select class="rc-calc__select" id="' + opts.id + '">' +
        '<option value="">' + esc(opts.placeholder || _t("Select...")) + "</option>" +
        optionsHtml +
        "</select>"
    );
}

// Options are plain strings (value === label) or {value, label} pairs, like select().
// Use pairs when the stored value must not depend on the page language.
function radioGroup(opts) {
    return (
        '<div class="rc-calc__radio-group" id="' + opts.id + '" data-radio-group="' + opts.id + '" role="radiogroup">' +
        (opts.options || []).map(function (o) {
            var val = typeof o === "string" ? o : o.value;
            var label = typeof o === "string" ? o : o.label;
            var active = opts.value === val ? " btn btn--s rc-calc__radio-pill--active" : "";
            return (
                '<button type="button" class="btn btn--s rc-calc__radio-pill' + active + '" data-radio-value="' + esc(val) + '"' +
                ' role="radio" aria-checked="' + (opts.value === val ? "true" : "false") + '">' + esc(label) + "</button>"
            );
        }).join("") +
        "</div>"
    );
}

// label is raw HTML by design (callers build it from translated strings that may
// embed markup, e.g. a required-asterisk <span>) — never pass unescaped user input here.
// Markup follows the site's shared .form-group--checkbox pattern (assets/scss/base/ui-kit--forms.scss):
// input and label are SIBLINGS (not label-wraps-input) — the checkbox itself is visually
// hidden and the label's :before/:after draw the box + checkmark via sibling selectors.
function checkboxRow(opts) {
    return (
        '<div class="form-group--checkbox rc-calc__checkbox-row">' +
        '<input type="checkbox" id="' + opts.id + '"' + (opts.checked ? " checked" : "") + ">" +
        '<label for="' + opts.id + '"><span>' + opts.label + "</span></label>" +
        "</div>"
    );
}

function card(innerHtml, extraClass) {
    return '<div class="rc-calc__card' + (extraClass ? " " + extraClass : "") + '">' + innerHtml + "</div>";
}

// A real heading (not a div) so screen-reader users can jump between results
// subsections ("Where that lost revenue may be coming from", etc.) via heading navigation.
function sectionLabel(text) {
    return '<h3 class="rc-calc__section-label"><span class="rc-calc__section-caption">' + esc(text) + "</span></h3>";
}

function h2(text) {
    return '<h3 class="title rc-calc__heading">' + esc(text) + "</h3>";
}

function sub(text) {
    return '<p class="p rc-calc__sub">' + esc(text) + "</p>";
}

function stepLabel(text) {
    return '<div class="rc-calc__step-label">' + esc(text) + "</div>";
}

function progressBar(percent) {
    return (
        '<div class="rc-calc__progress" role="progressbar" aria-valuenow="' + percent + '" aria-valuemin="0" aria-valuemax="100">' +
        '<div class="rc-calc__progress-bar" style="width:' + percent + '%"></div>' +
        "</div>"
    );
}

function primaryButton(opts) {
    return (
        '<div class="btn-bar rc-calc__btn-bar">' +
			'<div class="btn-bar--row justify-center">' +
				'<button type="button" class="btn btn--alt rc-calc__btn rc-calc__btn--primary" id="' + opts.id + '"' +
				(opts.disabled ? " disabled" : "") + ">" + esc(opts.label) + "</button>" +
			"</div>" +
        "</div>"
    );
}

function backButton() {
    return '<button type="button" class="link link--secondary rc-calc__back-btn" data-rc-back>' + esc(_t("Back")) + "</button>";
}

module.exports = {
    esc: esc, field: field, fieldError: fieldError, textInput: textInput, select: select,
    radioGroup: radioGroup, checkboxRow: checkboxRow, card: card, sectionLabel: sectionLabel,
    h2: h2, sub: sub, stepLabel: stepLabel, progressBar: progressBar, primaryButton: primaryButton,
    backButton: backButton
};
